import apiClient from './apiClient';
import { BaseService } from './BaseService';

// ========== INTERFACES ==========

export interface SignalDevicesResponse {
  devices: SignalDevice[];
  total: number;
}

export interface SignalService {
  id: number;
  name: string;
  description: string;
}

export interface SignalRegion {
  id: number;
  name: string;
  description: string;
}
export interface SignalRegionsResponse {
  regions: SignalRegion[];
  total: number;
}

export interface SignalFilm {
  id: number;
  name: string;
}

export interface SignalSupportType {
  type: string;
}

export interface SignalSupport {
  id: number;
  type: string;
  code: string;
  name: string;
}

export interface SignalDevice {
  id: number;
  type: string;
  symbol_name: string;
  code: string;
  name: string;
  description: string;
}

export interface SignalType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface CreateImageMarkerRequest {
  project_image_id: number;
  service_id: number;
  signal_device_id: number;
  signal_film_id: number;
  signal_support_type: string | null;
  signal_support_id: number | null;
  signal_regions_id: number | null; // ✅ NOVO
  signal_region_area: string | null; // ✅ NOVO
  comments: string;
  x_coordinate: number;
  y_coordinate: number;
  lat: number;
  long: number;
  type: string;
  adjusted_lat?: number;
  adjusted_lng?: number;
  horizontal_offset?: number;
  depth_offset?: number;
}

export interface ImageMarker {
  id: number;
  project_image_id: number;
  service_id: number;
  signal_device_id: number;
  signal_film_id: number;
  signal_support_type: string | null;
  signal_support_id: number | null;
  signal_regions_id: number | null; // ✅ NOVO
  signal_region_area: string | null; // ✅ NOVO
  comments: string | null;
  x_coordinate: number;
  y_coordinate: number;
  lat: number;
  long: number;
  type?: string;
  created_at: string;
  updated_at: string;
  signal_device?: SignalDevice;
  signal_support?: SignalSupport;
  signal_service?: SignalService;
  signal_film?: SignalFilm;
  signal_region?: SignalRegion; // ✅ NOVO
  adjusted_lat?: number;
  adjusted_lng?: number;
  horizontal_offset?: number;
  depth_offset?: number;
}

export interface VideoProject {
  id: number;
  name: string;
  description: string;
  rate: number;
  synced: boolean;
  video_filename: string | null;
  user: {
    id: number;
    name: string;
  };
  total_images: number;
}

export interface ProjectImage {
  id: number;
  project_id: number;
  filename: string;
  filetype: string;
  size: number;
  filedata: string;
  timestamp: string;
  lat: number | null;
  long: number | null;
  viewed: boolean;
  resolution: string;
  bearing: number | null;
  yaw: number | null;
  pitch: number | null;
  roll: number | null;
}

export interface CreateVideoProjectRequest {
  name: string;
  description: string;
  rate: number;
}

export interface UpdateVideoProjectRequest {
  name?: string;
  description?: string;
  progress?: number;
}

export interface ProjectImagesResponse {
  images: ProjectImage[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

interface SignalServicesResponse {
  services: SignalService[];
  total: number;
}

interface SignalFilmsResponse {
  films: SignalFilm[];
  total: number;
}

interface SignalSupportTypesResponse {
  types: SignalSupportType[];
  total: number;
}

interface SignalSupportsResponse {
  supports: SignalSupport[];
  total: number;
}

interface VideoUrlResponse {
  project_id: string;
  project_name: string;
  video_filename: string;
  video_url: string;
  direct_access: boolean;
  authentication_required: boolean;
}

interface ImageResponse {
  filedata: string;
  mime_type?: string;
  filetype?: string;
}

// ========== CACHE MANAGER ==========

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class CacheManager {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private readonly DEFAULT_TTL = 30 * 60 * 1000; // 30 minutos

  set<T>(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.DEFAULT_TTL
    });
    console.log(`📦 Cache SET: ${key}`, {
      dataSize: Array.isArray(data) ? data.length : 'object',
      ttl: ttl || this.DEFAULT_TTL
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      console.log(`📦 Cache MISS: ${key} - não encontrado`);
      return null;
    }

    const isExpired = Date.now() - entry.timestamp > entry.ttl;

    if (isExpired) {
      console.log(`📦 Cache EXPIRED: ${key}`);
      this.cache.delete(key);
      return null;
    }

    console.log(`📦 Cache HIT: ${key}`, {
      age: Math.floor((Date.now() - entry.timestamp) / 1000) + 's',
      dataSize: Array.isArray(entry.data) ? entry.data.length : 'object'
    });
    return entry.data as T;
  }

  clear(): void {
    const size = this.cache.size;
    this.cache.clear();
    console.log(`📦 Cache CLEARED: ${size} entries removed`);
  }

  clearPattern(pattern: string): void {
    const keys = Array.from(this.cache.keys());
    let cleared = 0;

    keys.forEach(key => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        cleared++;
      }
    });

    if (cleared > 0) {
      console.log(
        `📦 Cache PATTERN CLEAR: ${cleared} entries with "${pattern}" removed`
      );
    }
  }
}

// ========== SERVICE CLASS ==========

export class VideoProjectService extends BaseService<
  VideoProject,
  CreateVideoProjectRequest,
  UpdateVideoProjectRequest
> {
  private cache: CacheManager;
  private pendingRequests: Map<string, Promise<any>> = new Map();

  constructor() {
    super('/projects');
    this.cache = new CacheManager();
  }

  // ========== HELPER: Deduplicação de Requisições ==========

  private async deduplicatedRequest<T>(
    cacheKey: string,
    requestFn: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    const cachedData = this.cache.get<T>(cacheKey);
    if (cachedData !== null) {
      return cachedData;
    }

    if (this.pendingRequests.has(cacheKey)) {
      console.log(`⏳ Aguardando requisição em andamento: ${cacheKey}`);
      return this.pendingRequests.get(cacheKey)!;
    }

    const requestPromise = requestFn()
      .then(data => {
        this.cache.set(cacheKey, data, ttl);
        this.pendingRequests.delete(cacheKey);
        return data;
      })
      .catch(error => {
        this.pendingRequests.delete(cacheKey);
        throw error;
      });

    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  // ========== SIGNAL SERVICES ==========

  async getSignalServices(): Promise<SignalService[]> {
    return this.deduplicatedRequest(
      'signal_services',
      async () => {
        try {
          console.log('🔄 Buscando signal services da API...');
          const response = await apiClient.get<SignalServicesResponse>(
            '/signals/services'
          );
          console.log('Signal services response:', response);

          if (
            response.data?.services &&
            Array.isArray(response.data.services)
          ) {
            return response.data.services;
          }
          return [];
        } catch (error) {
          console.error('Error fetching signal services:', error);
          return [];
        }
      },
      30 * 60 * 1000
    );
  }

  async getSignalDevices(): Promise<SignalDevice[]> {
    return this.deduplicatedRequest('signal_devices', async () => {
      try {
        console.log('🔄 Buscando signal devices da API...');
        const response = await apiClient.get<SignalDevicesResponse>(
          '/signals/devices'
        );

        if (response.data?.devices && Array.isArray(response.data.devices)) {
          return response.data.devices;
        }
        return [];
      } catch (error) {
        console.error('Error fetching signal devices:', error);
        return [];
      }
    });
  }

  async getSignalDevicesByType(type: string): Promise<SignalDevice[]> {
    const cacheKey = `signal_devices_type_${type}`;

    return this.deduplicatedRequest(cacheKey, async () => {
      try {
        const allDevices = await this.getSignalDevices();
        return allDevices.filter(device => device.type === type);
      } catch (error) {
        console.error('Error filtering signal devices by type:', error);
        return [];
      }
    });
  }

  async getSignalFilms(): Promise<SignalFilm[]> {
    return this.deduplicatedRequest('signal_films', async () => {
      try {
        console.log('🔄 Buscando signal films da API...');
        const response = await apiClient.get<SignalFilmsResponse>(
          '/signals/films'
        );

        if (response.data?.films && Array.isArray(response.data.films)) {
          return response.data.films;
        }
        return [];
      } catch (error) {
        console.error('Error fetching signal films:', error);
        return [];
      }
    });
  }

  async getSignalRegions(): Promise<SignalRegion[]> {
    const cacheKey = 'signal_regions';
    const cached = this.cache.get<SignalRegion[]>(cacheKey);
    if (cached) return cached;
    const response = await apiClient.get<SignalRegionsResponse>(
      '/signals/regions/'
    );
    const regions = response.data.regions || [];
    this.cache.set(cacheKey, regions, 3600000); // 1 hora
    return regions;
  }

  async getSignalSupportTypes(): Promise<string[]> {
    return this.deduplicatedRequest('signal_support_types', async () => {
      try {
        console.log('🔄 Buscando signal support types da API...');
        const response = await apiClient.get<SignalSupportTypesResponse>(
          '/signals/supports/types'
        );

        if (response.data?.types && Array.isArray(response.data.types)) {
          return response.data.types.map((t: SignalSupportType) => t.type);
        }
        return [];
      } catch (error) {
        console.error('Error fetching signal support types:', error);
        return [];
      }
    });
  }

  async getSignalSupportsByType(type: string): Promise<SignalSupport[]> {
    const cacheKey = `signal_supports_${type}`;

    return this.deduplicatedRequest(
      cacheKey,
      async () => {
        try {
          console.log(
            `🔄 Buscando signal supports do tipo "${type}" da API...`
          );
          const response = await apiClient.get<SignalSupportsResponse>(
            `/signals/supports?type=${type}`
          );

          if (
            response.data?.supports &&
            Array.isArray(response.data.supports)
          ) {
            return response.data.supports;
          }
          return [];
        } catch (error) {
          console.error('Error fetching signal supports by type:', error);
          return [];
        }
      },
      60 * 60 * 1000
    );
  }

  async getAllSignalSupports(): Promise<SignalSupport[]> {
    return this.deduplicatedRequest(
      'all_signal_supports',
      async () => {
        try {
          const types = await this.getSignalSupportTypes();
          console.log('🔍 Tipos de suporte encontrados:', types);

          const allSupports: SignalSupport[] = [];

          const supportPromises = types.map(type =>
            this.getSignalSupportsByType(type)
          );
          const supportsByType = await Promise.all(supportPromises);

          supportsByType.forEach((supports, index) => {
            console.log(
              `🔍 Suportes do tipo "${types[index]}": ${supports.length} items`
            );
            allSupports.push(...supports);
          });

          console.log(`🔍 TOTAL de suportes carregados: ${allSupports.length}`);
          return allSupports;
        } catch (error) {
          console.error('Error fetching all signal supports:', error);
          return [];
        }
      },
      60 * 60 * 1000
    );
  }

  async getSignalSupportById(
    supportId: number
  ): Promise<SignalSupport | undefined> {
    try {
      const allSupports = await this.getAllSignalSupports();
      const found = allSupports.find(s => s.id === supportId);

      if (found) {
        console.log(`✅ Support ID ${supportId} encontrado no cache`);
        return found;
      }

      console.warn(`⚠️ Support ID ${supportId} não encontrado`);
      return undefined;
    } catch (error) {
      console.error(`Error fetching support ${supportId}:`, error);
      return undefined;
    }
  }

  // ========== CACHE MANAGEMENT ==========

  clearCache(): void {
    this.cache.clear();
    console.log('🧹 Todo o cache foi limpo');
  }

  clearSignalCache(): void {
    this.cache.clearPattern('signal_');
    console.log('🧹 Cache de signals limpo');
  }

  // ========== MARKERS ==========

  async createImageMarker(
    marker: CreateImageMarkerRequest
  ): Promise<ImageMarker> {
    this.cache.clearPattern('markers_project_');

    const response = await apiClient.post<ImageMarker | { data: ImageMarker }>(
      '/markers',
      marker
    );
    console.log('Create marker response:', response);

    if (response.data && typeof response.data === 'object') {
      if ('id' in response.data) {
        return response.data as ImageMarker;
      }

      if ('data' in response.data) {
        const dataResponse = response.data as { data?: ImageMarker };
        if (dataResponse.data) {
          return dataResponse.data;
        }
      }
    }

    throw new Error('Unexpected create marker response structure');
  }

  async updateImageMarker(
    markerId: number,
    marker: Partial<CreateImageMarkerRequest>
  ): Promise<ImageMarker> {
    this.cache.clearPattern('markers_project_');

    const response = await apiClient.patch<any>(`/markers/${markerId}`, marker);
    console.log('Update marker response:', response);

    if (response.data && response.data.marker) {
      return response.data.marker as ImageMarker;
    }

    if (response.data && response.data.id) {
      return response.data as ImageMarker;
    }

    if (response.status === 200) {
      return {
        id: markerId,
        ...marker
      } as ImageMarker;
    }

    throw new Error('Unexpected update marker response structure');
  }

  async deleteImageMarker(markerId: number): Promise<void> {
    this.cache.clearPattern('markers_project_');

    const response = await apiClient.delete(`/markers/${markerId}`);
    console.log('Delete marker response:', response);

    if (response.status >= 200 && response.status < 300) {
      return;
    }

    throw new Error(`Failed to delete marker: ${response.status}`);
  }

  async getProjectMarkers(projectId: number | string): Promise<ImageMarker[]> {
    const cacheKey = `markers_project_${projectId}`;

    const markers = await this.deduplicatedRequest(
      cacheKey,
      async () => {
        console.log(`🔄 Buscando markers do projeto ${projectId} da API...`);
        const response = await apiClient.get<
          { markers: ImageMarker[]; total: number } | ImageMarker[]
        >(`/markers/project/${projectId}`);

        if (
          response.data &&
          typeof response.data === 'object' &&
          'markers' in response.data
        ) {
          const wrappedData = response.data as {
            markers: ImageMarker[];
            total: number;
          };
          if (Array.isArray(wrappedData.markers)) {
            console.log(
              `✅ Loaded ${wrappedData.markers.length} markers from API`
            );
            return wrappedData.markers;
          }
        }

        if (Array.isArray(response.data)) {
          return response.data;
        }

        console.warn('Unexpected markers response structure:', response.data);
        return [];
      },
      5 * 60 * 1000
    );

    if (markers.length > 0) {
      console.log('📦 Pré-carregando devices e supports para markers...');
      await this.getSignalDevices();
      await this.getAllSignalSupports();
      console.log('✅ Devices e supports carregados no cache');
    }

    return markers;
  }

  // ========== HELPERS ==========

  getUniqueDeviceTypes(devices: SignalDevice[]): string[] {
    const types = new Set<string>();
    devices.forEach(device => types.add(device.type));
    return Array.from(types).sort();
  }

  getSignalDeviceImagePath(device: SignalDevice): string {
    return `/images/signs/${device.symbol_name}`;
  }

  // ========== PROJECT METHODS ==========

  async delete(id: number | string): Promise<void> {
    this.cache.clearPattern(`project_${id}`);
    const response = await apiClient.delete(`/projects/${id}`);
    console.log('Delete response:', response);
  }

  async getAll(): Promise<VideoProject[]> {
    return this.deduplicatedRequest(
      'all_projects',
      async () => {
        console.log('🔄 Buscando todos os projetos da API...');
        const response = await apiClient.get<
          VideoProject[] | { data: VideoProject[] }
        >('/projects');

        if (Array.isArray(response.data)) {
          return response.data;
        }

        if (
          response.data &&
          typeof response.data === 'object' &&
          'data' in response.data
        ) {
          const wrappedData = response.data as { data: VideoProject[] };
          if (Array.isArray(wrappedData.data)) {
            return wrappedData.data;
          }
        }

        console.warn('Unexpected response structure:', response.data);
        return [];
      },
      2 * 60 * 1000
    );
  }

  async getProjectImages(projectId: string): Promise<ProjectImage[]> {
    const cacheKey = `project_images_${projectId}`;

    return this.deduplicatedRequest(
      cacheKey,
      async () => {
        console.log(`🔄 Buscando imagens do projeto ${projectId} da API...`);
        const response = await apiClient.get<
          ProjectImage[] | ProjectImagesResponse | { data: ProjectImage[] }
        >(`/projects/${projectId}/images/info`);

        if (Array.isArray(response.data)) {
          return response.data;
        }

        if (response.data && typeof response.data === 'object') {
          if ('images' in response.data) {
            const imagesResponse = response.data as ProjectImagesResponse;
            if (Array.isArray(imagesResponse.images)) {
              return imagesResponse.images;
            }
          }

          if ('data' in response.data) {
            const wrappedData = response.data as { data: ProjectImage[] };
            if (Array.isArray(wrappedData.data)) {
              return wrappedData.data;
            }
          }
        }

        console.warn('Unexpected images response structure:', response.data);
        return [];
      },
      10 * 60 * 1000
    );
  }

  async getVideoUrl(projectId: string): Promise<VideoUrlResponse> {
    const response = await apiClient.get<VideoUrlResponse>(
      `/projects/${projectId}/video-url`
    );
    console.log('Video URL response:', response);
    return response.data;
  }

  getFormattedFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFormattedTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString();
  }

  async uploadImages(
    projectId: string | number,
    images: {
      filename: string;
      filetype: string;
      filesize: number;
      timestamp: string;
      geo: {
        lat: number;
        long: number;
      };
      filedata: string;
    }[]
  ): Promise<void> {
    this.cache.clearPattern(`project_images_${projectId}`);
    await apiClient.post(`/projects/${projectId}/images`, images);
  }

  async listProjectImages(projectId: string | number): Promise<ProjectImage[]> {
    const cacheKey = `project_images_list_${projectId}`;

    return this.deduplicatedRequest(
      cacheKey,
      async () => {
        console.log(`🔄 Listando imagens do projeto ${projectId} da API...`);
        const response = await apiClient.get<
          { images?: ProjectImage[] } | ProjectImage[]
        >(`/projects/${projectId}/images/list`);

        if (Array.isArray(response.data)) {
          return response.data;
        }

        if (
          response.data &&
          typeof response.data === 'object' &&
          'images' in response.data
        ) {
          const imagesResponse = response.data as { images?: ProjectImage[] };
          if (imagesResponse.images && Array.isArray(imagesResponse.images)) {
            return imagesResponse.images;
          }
        }

        console.warn(
          'Unexpected list images response structure:',
          response.data
        );
        return [];
      },
      5 * 60 * 1000
    );
  }

  async deleteProjectImage(
    projectId: string | number,
    imageId: number
  ): Promise<void> {
    this.cache.clearPattern(`project_images_${projectId}`);

    const response = await apiClient.delete(
      `/projects/${projectId}/image/${imageId}`
    );
    console.log('Delete image response:', response);

    if (response.status >= 200 && response.status < 300) {
      return;
    }

    throw new Error(`Failed to delete image: ${response.status}`);
  }

  async generateVideo(projectId: number | string): Promise<void> {
    await apiClient.post(`/projects/${projectId}/create-video`);
  }

  // ========== IMAGE LOADING HELPERS ==========

  /**
   * Carrega uma imagem de um projeto
   * @param projectId ID do projeto
   * @param imageId ID da imagem
   * @returns URL da imagem (base64 ou blob URL)
   */
  async getProjectImage(
    projectId: number | string,
    imageId: number
  ): Promise<string> {
    try {
      console.log(
        `[VideoService] 🔄 Carregando imagem: projeto ${projectId}, imagem ${imageId}`
      );

      const response = await apiClient.get<ImageResponse>(
        `/projects/${projectId}/image/${imageId}`,
        { responseType: 'json' }
      );

      if (response.data && response.data.filedata) {
        const mimeType =
          response.data.mime_type || response.data.filetype || 'image/jpeg';
        const imageData = `data:${mimeType};base64,${response.data.filedata}`;
        console.log('[VideoService] ✅ Imagem carregada (base64)');
        return imageData;
      }

      console.log('[VideoService] ⚠️ Tentando blob...');
      const blobResponse = await apiClient.get<Blob>(
        `/projects/${projectId}/image/${imageId}`,
        { responseType: 'blob' }
      );

      const blob = blobResponse.data;
      const blobUrl = URL.createObjectURL(blob);
      console.log('[VideoService] ✅ Imagem carregada (blob)');
      return blobUrl;
    } catch (error) {
      console.error(
        `[VideoService] ❌ Erro ao carregar imagem ${imageId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Carrega múltiplas imagens de uma vez
   * @param projectId ID do projeto
   * @param imageIds Array de IDs de imagens
   * @returns Map<imageId, imageUrl>
   */
  async getProjectImagesMap(
    projectId: number | string,
    imageIds: number[]
  ): Promise<Map<number, string>> {
    const imageMap = new Map<number, string>();

    console.log(
      `[VideoService] 📦 Carregando ${imageIds.length} imagens do projeto ${projectId}`
    );

    const BATCH_SIZE = 5;
    for (let i = 0; i < imageIds.length; i += BATCH_SIZE) {
      const batch = imageIds.slice(i, i + BATCH_SIZE);
      const promises = batch.map(async imageId => {
        try {
          const url = await this.getProjectImage(projectId, imageId);
          imageMap.set(imageId, url);
        } catch (error) {
          console.error(
            `[VideoService] ❌ Falha ao carregar imagem ${imageId}`,
            error
          );
        }
      });

      await Promise.all(promises);
    }

    console.log(
      `[VideoService] ✅ ${imageMap.size}/${imageIds.length} imagens carregadas`
    );

    return imageMap;
  }
}

export const videoProjectService = new VideoProjectService();
